"use client";
import React, { useState, useEffect } from 'react';
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { Hammer, ChevronLeft, ChevronRight } from 'lucide-react';
import RealizationService from '@/app/services/realizations';
import { Realization, RealizationCategory } from '@/app/_types/realizations';
import convertStringToSlug from '@/app/lib/urlFormatter';

type PaginationMeta = {
    current_page: number;
    per_page: number;
    total: number;
};

function LoadingSkeleton() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-8xl mx-auto p-6 md:p-14">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white animate-pulse">
                    <div className="bg-slate-200 w-full h-56" />
                    <div className="p-5">
                        <div className="bg-slate-200 h-3 w-1/3 mb-3 rounded" />
                        <div className="bg-slate-200 h-5 w-2/3 mb-2 rounded" />
                        <div className="bg-slate-200 h-4 w-full rounded" />
                    </div>
                </div>
            ))}
        </div>
    );
}

function EmptyState({ t }: { t: ReturnType<typeof useTranslations> }) {
    return (
        <div className="h-[500px] flex flex-col items-center justify-center text-stone-600">
            <div className="p-4 bg-white shadow-xl rounded-2xl border border-stone-200 mb-6">
                <Hammer size={48} strokeWidth={1.5} className="text-stone-700" />
            </div>
            <h2 className="text-3xl font-light uppercase tracking-tight mb-3">{t('no_realizations')}</h2>
            <p className="text-stone-400 text-base">{t('no_realizations_desc')}</p>
        </div>
    );
}

function ErrorState({ t }: { t: ReturnType<typeof useTranslations> }) {
    return (
        <div className="max-w-8xl px-6 h-[400px] flex flex-col items-center justify-center">
            <p className="text-stone-400 text-base">{t('load_error')}</p>
        </div>
    );
}

function Pagination({ meta, page, onPageChange }: { meta: PaginationMeta; page: number; onPageChange: (p: number) => void }) {
    const totalPages = Math.ceil(meta.total / meta.per_page);
    if (totalPages <= 1) return null;

    const getPages = (): (number | '...')[] => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const pages: (number | '...')[] = [1];
        if (page > 3) pages.push('...');
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
            pages.push(i);
        }
        if (page < totalPages - 2) pages.push('...');
        pages.push(totalPages);
        return pages;
    };

    return (
        <div className="flex items-center justify-center gap-1 py-10">
            <button
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
                className="w-9 h-9 flex items-center justify-center rounded-full text-stone-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
                <ChevronLeft size={16} />
            </button>

            {getPages().map((p, i) =>
                p === '...' ? (
                    <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-stone-400 text-sm">
                        …
                    </span>
                ) : (
                    <button
                        key={p}
                        onClick={() => onPageChange(p as number)}
                        className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                            p === page
                                ? 'bg-black text-white'
                                : 'text-stone-600 hover:bg-slate-100'
                        }`}
                    >
                        {p}
                    </button>
                )
            )}

            <button
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-full text-stone-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
                <ChevronRight size={16} />
            </button>
        </div>
    );
}

function RealizationCard({ item, locale }: { item: Realization; locale: string }) {
    const coverUrl = item.cover_image?.[0]?.url?.replace('https://', 'https://');
    const isHorizontal = item.orientation === 'horizontal';
    const categorySlug = convertStringToSlug(item.category[`name_${locale}` as keyof RealizationCategory] as string);
    const nameSlug = convertStringToSlug(item.translations[locale]?.name ?? '');
    const href = `/services/${categorySlug}/${nameSlug}/${item.id}`;

    return (
        <Link href={href} className="bg-white flex flex-col group cursor-pointer">
            {coverUrl && (
                <div
                    className="relative w-full overflow-hidden"
                    style={{ aspectRatio: isHorizontal ? '16/9' : '3/4' }}
                >
                    <Image
                        src={coverUrl}
                        fill
                        quality={65}
                        alt={item.translations[locale]?.name ?? ''}
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    {item.completion_year && (
                        <span className="absolute top-3 right-3 bg-white/90 text-black text-xs font-medium px-3 py-1 rounded-full">
                            {item.completion_year}
                        </span>
                    )}
                </div>
            )}
            <div className="p-5 flex flex-col flex-1 justify-between">
                <div>
                    <span className="text-xs uppercase tracking-widest text-stone-400 mb-1 mt-2 block">
                        {item.category[`name_${locale}` as keyof RealizationCategory] as string}
                    </span>
                    <h3 className="text-lg font-medium text-stone-800 mb-2 leading-snug">
                        {item.translations[locale]?.name}
                    </h3>
                    {item.translations[locale]?.description && (
                        <p className="text-stone-400 text-sm leading-relaxed line-clamp-3">
                            {item.translations[locale].description}
                        </p>
                    )}
                </div>
            </div>
        </Link>
    );
}

export default function RealizationsList() {
    const t = useTranslations('Realizations');
    const locale = useLocale();
    const [data, setData] = useState<Realization[]>([]);
    const [meta, setMeta] = useState<PaginationMeta | null>(null);
    const [categories, setCategories] = useState<RealizationCategory[]>([]);
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    async function fetchRealizations(targetPage: number) {
        setIsLoading(true);
        try {
            const params: any = { language: locale, page: targetPage };
            if (activeCategory) params.category_id = activeCategory;

            const result: any = await RealizationService.getList(params);
            if (result?.data?.success) {
                setData(result.data.data ?? []);
                setMeta(result.data.meta ?? null);
                setError(false);
            } else {
                setError(true);
            }
        } catch {
            setError(true);
        } finally {
            setIsLoading(false);
        }
    }

    async function fetchCategories() {
        try {
            const result: any = await RealizationService.getCategories({ language: locale });
            if (result?.data?.success) {
                setCategories(result.data.data ?? []);
            }
        } catch {
            // categories are optional; fail silently
        }
    }

    function handleCategoryChange(categoryId: string | null) {
        setActiveCategory(categoryId);
        setPage(1);
    }

    function handlePageChange(newPage: number) {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    useEffect(() => {
        fetchCategories();
    }, [locale]);

    useEffect(() => {
        fetchRealizations(page);
    }, [page, activeCategory, locale]);

    return (
        <section className="w-full mt-10 md:mt-0">
            <div className="flex flex-wrap gap-2 px-6 md:px-14 py-6 border-b border-slate-100">
                <button
                    onClick={() => handleCategoryChange(null)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        !activeCategory
                            ? 'bg-black text-white'
                            : 'bg-slate-100 text-stone-600 hover:bg-slate-200'
                    }`}
                >
                    {t('all_categories')}
                </button>
                {categories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.id)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            activeCategory === cat.id
                                ? 'bg-black text-white'
                                : 'bg-slate-100 text-stone-600 hover:bg-slate-200'
                        }`}
                    >
                        {cat[`name_${locale}` as keyof RealizationCategory] as string}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <LoadingSkeleton />
            ) : error ? (
                <ErrorState t={t} />
            ) : data.length === 0 ? (
                <EmptyState t={t} />
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-8xl mx-auto p-6 md:p-14">
                        {data.map((item) => (
                            <RealizationCard key={item.id} item={item} locale={locale} />
                        ))}
                    </div>
                    {meta && (
                        <Pagination meta={meta} page={page} onPageChange={handlePageChange} />
                    )}
                </>
            )}
        </section>
    );
}
